import { useRef } from "react";

import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    TableToolbar,
    TextTool,
    TSTExpandableTable,
    TSTFilterBar,
    TSTPagination,
} from "@impero-com/ui";
import { useOutletContext } from "react-router-dom";

import { RemoveUserModal } from "org/user/components/RemoveUserModal";
import { ReplaceUserModal } from "org/user/components/ReplaceUserModal";
import { usePartialControlsForUserFromGroupsQuery } from "org/user/queries/usePartialControlsForUserFromGroupsQuery";
import { useUserGroupsOverviewForUserQuery } from "org/user/queries/useUserGroupsOverviewForUserQuery";
import { OutletContext } from "org/user/UserViewPage";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { useRemoveGroupAccessRightUtils } from "./useRemoveGroupAccessRightUtils";
import { useReplaceGroupUserUtils } from "./useReplaceGroupUserUtils";
import { UserGroupControlsSubTable } from "./UserGroupControlsSubTable";
import { GroupRow, useUserGroupsListTable } from "./useUserGroupsListTable";

export function UserGroupsList() {
    const { user } = useOutletContext<OutletContext>();
    const userId = user.id;
    const { t } = useTranslation();

    const { data: groups } = useUserGroupsOverviewForUserQuery(userId);
    const { data: partialControls } =
        usePartialControlsForUserFromGroupsQuery(userId);

    const { table, getFilterComponent, tableId } = useUserGroupsListTable(
        groups,
        partialControls
    );
    const tableRef = useRef<HTMLDivElement>(null);

    const modeConfig = getFilterModeConfig(t);

    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const deselectAll = () => table.resetRowSelection();

    const {
        removeAccessRightToolState,
        isRemoveAccessRightModalOpen,
        openRemoveAccessRightModal,
        closeRemoveAccessRightModal,
        removeAccessRight,
        isRemovingAccessRight,
    } = useRemoveGroupAccessRightUtils({ userId, selectedRows, deselectAll });

    const {
        replaceUserToolState,
        isReplaceUserModalOpen,
        openReplaceUserModal,
        closeReplaceUserModal,
        replaceUser,
        isReplacingUser,
    } = useReplaceGroupUserUtils({ userId, selectedRows, deselectAll });

    return (
        <div>
            <RemoveUserModal
                onClose={closeRemoveAccessRightModal}
                onSubmit={() => void removeAccessRight()}
                open={isRemoveAccessRightModalOpen}
                loading={isRemovingAccessRight}
                message={t("AccessAndAssignment_RemoveGroupDialog_Text", {
                    user: formatUserName(user),
                    count: selectedRows.length,
                })}
            />
            <ReplaceUserModal
                open={isReplaceUserModalOpen}
                replaceType="WithUser"
                loading={isReplacingUser}
                onClose={closeReplaceUserModal}
                onClick={(userOrGroup) => void replaceUser(userOrGroup)}
                user={user}
            />
            <TSTFilterBar<GroupRow>
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                modeConfig={modeConfig}
            />
            <TSTExpandableTable<GroupRow>
                tableRef={tableRef}
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={removeAccessRightToolState}
                            onClick={openRemoveAccessRightModal}
                        >
                            {t("AccessAndAssignment_Tool_RemoveUserFromGroups")}
                        </TextTool>
                        <TextTool
                            state={replaceUserToolState}
                            onClick={openReplaceUserModal}
                        >
                            {t("AccessAndAssignment_Tool_ReplaceUser")}
                        </TextTool>
                    </TableToolbar>
                }
                renderSubComponent={(row) => (
                    <UserGroupControlsSubTable controls={row.controls} />
                )}
            />
            <TSTPagination
                table={table}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
                tableElement={tableRef}
            />
        </div>
    );
}
